import React, { useContext, useEffect, useState, useRef } from 'react'
import MainNav from '../../Components/Navbar';
import { Container, Table, Spinner, Alert, Badge, Button, Modal, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import CustomSelect from '../../Components/CustomSelect';
import GlobalContext from '../../Components/GlobalContext';
import { firebaseToString } from '../../Tools/tools';
import useCompanies from "../../hooks/useCompanies";
import { listFiles, uploadFile } from "../../Components/oneDrive";
import { FileText, Download, Trash, Edit, Eye, Save, X } from "lucide-react";
import axios from 'axios';
import { getDatabase, ref, set, onValue, off } from 'firebase/database';

function Arquivos() {
    const { globalData } = useContext(GlobalContext);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
    const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
    const startYear = 2024;
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const currentYear = new Date().getFullYear();
    const { companyNames, allCompanies } = useCompanies();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editingFileId, setEditingFileId] = useState(null);
    const [editedDescription, setEditedDescription] = useState('');
    const fileInputRef = useRef(null);
    const descriptionInputRef = useRef(null);

    const apiHost = globalData.apiHost;
    const authToken = globalData?.authToken || "";
    const db = getDatabase();

    const isAdmin = globalData.role === 'admin';

    useEffect(() => {
        if (globalData.role !== 'admin' && globalData.company) {
            setSelectedCompany(globalData.company);

            const companyData = allCompanies[globalData.company];
            if (companyData) {
                const deps = Object.entries(companyData).filter(([key, value]) => {
                    return (
                        key !== 'isCompanyVisible' && key !== 'instrutores' && value?.isDeptVisible === true
                    );
                }).map(([key]) => key);

                setDepartmentOptions(
                    deps.map(dep => ({ value: dep, label: firebaseToString(dep) }))
                );
            } else {
                setDepartmentOptions([]);
            }
        }
    }, [globalData, allCompanies]);

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';

        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getFileExtension = (filename) => {
        if (!filename) return '';
        const parts = filename.split('.');
        return parts.length > 1 ? parts.pop().toUpperCase() : '';
    };

    const handleDownloadFile = async (file) => {
        try {
            const response = await axios.get(
                `${apiHost}/onedrive/download/${file.id}/${selectedCompany}/${selectedDepartment || 'GERAL'}/${selectedYear}/${selectedMonth}`,
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: authToken,
                    }
                }
            );

            const blob = new Blob([response.data]);
            const downloadUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = file.name;
            a.style.display = 'none';

            a.onclick = () => {
                setTimeout(() => {
                    document.body.removeChild(a);
                    URL.revokeObjectURL(downloadUrl);
                }, 100);
            };

            document.body.appendChild(a);
            a.click();

        } catch (error) {
            alert('Erro ao baixar o arquivo: ' + error.message);
        }
    };

const handleViewFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
        alert('Apenas arquivos PDF podem ser visualizados.');
        return;
    }
    
    try {
        const filePath = `/public_html/arquivos-qualy/${selectedCompany}/${selectedDepartment || 'GERAL'}/${selectedYear}/${selectedMonth}/${file.name}`;
        
        const encodedFilePath = encodeURIComponent(filePath);
        
        const fileUrl = `${apiHost}/ftp/view?path=${encodedFilePath}`;
        
        window.open(fileUrl, '_blank');
        
    } catch (error) {
        alert('Erro ao visualizar o arquivo: ' + error.message);
    }
};

    const handleDeleteFile = async (file) => {
        setFileToDelete(file);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!fileToDelete) return;

        setDeleting(true);

        const deleteUrl = `${apiHost}/onedrive/delete/${fileToDelete.id}?empresa=${selectedCompany}&departamento=${selectedDepartment || 'GERAL'}&ano=${selectedYear}&mes=${selectedMonth}`;

        try {
            const response = await axios.delete(
                deleteUrl,
                {
                    headers: {
                        Authorization: authToken,
                    }
                }
            );

            const descriptionPath = `files-details/${selectedCompany}/${selectedYear}/${selectedMonth}/${selectedDepartment || 'GERAL'}/${fileToDelete.id}`;
            await set(ref(db, descriptionPath), null);

            setFiles(files.filter(f => f.id !== fileToDelete.id));
            alert('Arquivo deletado com sucesso!');

        } catch (error) {
            alert('Erro ao deletar o arquivo: ' + (error.response?.data?.error || error.message));
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setFileToDelete(null);
        }
    };

    const handleUploadClick = () => {
        if (!selectedCompany) {
            alert('Selecione uma empresa primeiro');
            return;
        }
        fileInputRef.current?.click();
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!selectedCompany || !selectedYear || !selectedMonth) {
            alert('Preencha todos os filtros (Empresa, Ano, Mês) antes de fazer upload');
            return;
        }

        setUploading(true);
        try {
            await uploadFile(apiHost, file, selectedCompany, selectedDepartment || 'GERAL', selectedYear, selectedMonth, authToken);

            alert('Arquivo enviado com sucesso!');
            fetchFiles();

        } catch (error) {
            alert('Erro ao enviar arquivo: ' + (error.response?.data?.error || error.message));
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDescriptionDoubleClick = (file) => {
        if (!isAdmin) return;

        setEditingFileId(file.id);
        setEditedDescription(file.description || '');

        setTimeout(() => {
            if (descriptionInputRef.current) {
                descriptionInputRef.current.focus();
            }
        }, 0);
    };

    const handleDescriptionChange = (e) => {
        setEditedDescription(e.target.value);
    };

    const saveDescription = async (fileId, fileName) => {
        try {
            const path = `files-details/${selectedCompany}/${selectedYear}/${selectedMonth}/${selectedDepartment || 'GERAL'}/${fileId}`;
            await set(ref(db, path), {
                description: editedDescription,
                fileName: fileName,
                lastUpdated: new Date().toISOString(),
            });
            setFiles(files.map(file =>
                file.id === fileId ? { ...file, description: editedDescription } : file
            ));

            setEditingFileId(null);
        } catch (_) {
        }
    };

    const cancelEdit = () => {
        setEditingFileId(null);
        setEditedDescription('');
    };

    const handleKeyPress = (e, fileId, fileName) => {
        if (e.key === 'Enter') {
            saveDescription(fileId, fileName);
        } else if (e.key === 'Escape') {
            cancelEdit();
        }
    };

    const fetchFiles = async () => {
        if (!selectedCompany) {
            setFiles([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await listFiles(apiHost, selectedCompany, selectedDepartment || 'GERAL', selectedYear, selectedMonth, authToken);
            const path = `files-details/${selectedCompany}/${selectedYear}/${selectedMonth}/${selectedDepartment || 'GERAL'}`;
            const filesRef = ref(db, path);

            const descriptions = await new Promise((resolve, reject) => {
                onValue(
                    filesRef,
                    (snapshot) => {
                        resolve(snapshot.val() || {});
                    },
                    (error) => {
                        reject(error);
                    },
                    { onlyOnce: true }
                );
            });

            const filesWithDescriptions = response.map(file => ({
                ...file,
                description: descriptions[file.id]?.description || ''
            }));

            setFiles(filesWithDescriptions);

        } catch (err) {
            setError("Erro ao carregar arquivos");
            console.error("Erro:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, [selectedCompany, selectedDepartment, selectedMonth, selectedYear, apiHost, authToken]);

    return (
        <>
            <MainNav />
            <div>
                <Container className="w-100 pt-3" style={{ minHeight: "90vh" }}>

                    <div className="mb-3 d-flex flex-wrap">
                        {globalData.role === 'admin' && (
                            <CustomSelect
                                options={companyNames
                                    .filter((name) => name !== 'id')
                                    .map((name) => ({ value: name, label: firebaseToString(name) }))}
                                value={selectedCompany}
                                onChange={(e) => {
                                    const companyValue = e.target.value;
                                    setSelectedCompany(companyValue);
                                    setSelectedDepartment('GERAL');
                                    const companyData = allCompanies[companyValue];

                                    if (companyData) {
                                        const deps = Object.entries(companyData)
                                            .filter(([key, value]) => {
                                                return (
                                                    key !== 'isCompanyVisible' &&
                                                    key !== 'instrutores' &&
                                                    value?.isDeptVisible === true
                                                );
                                            }).map(([key]) => key);

                                        setDepartmentOptions(
                                            deps.map(dep => ({ value: dep, label: firebaseToString(dep) })));
                                    } else {
                                        setDepartmentOptions([]);
                                    }
                                }}
                                placeholder="Empresa"
                                ariaLabel="select-company"
                                className="me-3"
                                style={{ width: '300px' }}
                            />
                        )}

                        <CustomSelect
                            options={departmentOptions}
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            placeholder="Departamento"
                            ariaLabel="select-department"
                            className="me-3"
                            style={{ width: '200px' }}
                        />

                        <CustomSelect
                            options={Array.from({ length: 12 }, (_, i) => {
                                const value = String(i + 1).padStart(2, '0');
                                const label = new Date(0, i).toLocaleString('pt-BR', { month: 'long' });
                                return { value, label: label.charAt(0).toUpperCase() + label.slice(1) };
                            })}
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            placeholder="Mês"
                            ariaLabel="select-month"
                            className="me-3"
                            style={{ width: '120px' }}
                        />

                        <CustomSelect
                            options={Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
                                const year = startYear + i;
                                return { value: String(year), label: String(year) };
                            })}
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                            placeholder="Ano"
                            ariaLabel="select-year"
                            className="me-3"
                            style={{ width: '80px' }}
                        />

                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                            accept=".doc,.docx,.xlsx,.ods, .odt, .pdf, .csv"
                        />

                        {isAdmin && (
                            <div className="">
                                <OverlayTrigger overlay={<Tooltip>Carrega no banco de dados um arquivo para o mês selecionado</Tooltip>}>
                                    <Button
                                        onClick={handleUploadClick}
                                        disabled={uploading || !selectedCompany}
                                        variant="primary"
                                    >
                                        {uploading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Enviando...
                                            </>
                                        ) : (
                                            'Upload'
                                        )}
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        )}
                    </div>

                    {uploading && (
                        <Alert variant="info" className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Enviando arquivo...
                        </Alert>
                    )}

                    {loading ? (
                        <div className="d-flex justify-content-center mt-5">
                            <Spinner animation="border" />
                        </div>
                    ) : error ? (
                        <Alert variant="danger">{error}</Alert>
                    ) : files.length === 0 ? (
                        <p className="text-muted">Nenhum arquivo encontrado para os filtros selecionados.</p>
                    ) : (
                        <Table bordered hover responsive className="mt-3">
                            <thead>
                                <tr>
                                    <th style={{ width: '25%' }}>Nome do Arquivo</th>
                                    <th style={{ width: '45%' }}>Descrição</th>
                                    <th style={{ width: '80px' }}>Formato</th>
                                    <th style={{ width: '100px' }}>Tamanho</th>
                                    <th style={{ width: '120px' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(files) && files.map((file) => (
                                    <tr key={file.id}>
                                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            <FileText size={18} className="me-2" />
                                            {file.name || "(sem nome)"}
                                        </td>
                                        <td
                                            onDoubleClick={() => handleDescriptionDoubleClick(file)}
                                            style={{
                                                cursor: isAdmin ? 'pointer' : 'default',
                                                wordWrap: 'break-word',
                                                whiteSpace: 'normal',
                                                minWidth: '200px'
                                            }}
                                        >
                                            {editingFileId === file.id ? (
                                                <div className="d-flex align-items-center">
                                                    <Form.Control
                                                        ref={descriptionInputRef}
                                                        type="text"
                                                        value={editedDescription}
                                                        onChange={handleDescriptionChange}
                                                        onKeyDown={(e) => handleKeyPress(e, file.id, file.name)}
                                                        className="me-2"
                                                        size="sm"
                                                    />
                                                    <button
                                                        className="btn btn-sm btn-outline-primary me-1"
                                                        onClick={() => saveDescription(file.id, file.name)}
                                                        title="Salvar descrição"
                                                    >
                                                        <Save size={14} />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={cancelEdit}
                                                        title="Cancelar edição"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        {file.description || <span className="text-muted">{isAdmin ? 'Clique duas vezes para editar' : 'Sem descrição'}</span>}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ width: '80px' }}>
                                            <Badge bg="secondary">
                                                {getFileExtension(file.name)}
                                            </Badge>
                                        </td>
                                        <td style={{ width: '100px' }}>
                                            {file.size ? formatFileSize(file.size) : 'N/A'}
                                        </td>
                                        <td style={{ width: '120px' }}>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleViewFile(file)}
                                                    title="Visualizar arquivo"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleDownloadFile(file)}
                                                    title="Baixar arquivo"
                                                >
                                                    <Download size={16} />
                                                </button>
                                                {isAdmin && (
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDeleteFile(file)}
                                                        title="Deletar arquivo"
                                                    >
                                                        <Trash size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}

                    <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirmar Exclusão</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Tem certeza que deseja excluir o arquivo:</p>
                            <p className="fw-bold">"{fileToDelete?.name}"</p>
                            <p className="text-muted">Esta ação não pode ser desfeita.</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="danger"
                                onClick={confirmDelete}
                                disabled={deleting}
                            >
                                {deleting ? 'Excluindo...' : 'Excluir'}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Container>
            </div>
        </>
    );
}

export default Arquivos;